import * as React from "react";
import ReactDOM from "react-dom"; // Import ReactDOM for portals
import {
    Chart,
    ChartSeries,
    ChartSeriesItem,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartTitle,
    ChartLegend,
    exportVisual,
    ChartValueAxis,
    ChartValueAxisItem,
    ChartSeriesDefaults,
} from "@progress/kendo-react-charts";
import configData from "../../config.json";
import { GetSession, GetToken } from "../../components/Session";
import { ExpansionPanel, ExpansionPanelContent } from "@progress/kendo-react-layout";
import { Reveal } from "@progress/kendo-react-animation";
import DashboardCards from "./DashboardCards";
import "hammerjs";
import { exportPDF } from "@progress/kendo-drawing";
import { saveAs } from "@progress/kendo-file-saver";
import { GetCloudLogin } from "../../WeighBridge/Helpers";

const cardStyle3D = {
    borderRadius: "10px",
    boxShadow:
        "0 6px 15px rgba(0, 0, 0, 0.25), inset 0 -3px 6px rgba(255, 255, 255, 0.3)",
    backgroundColor: "#fff",
};

const buttonStaticStyle = {
    borderRadius: "6px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    cursor: "pointer",
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    padding: "6px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

const modalStyles = {
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.6)",
        zIndex: 10000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
    },
    content: {
        backgroundColor: "white",
        borderRadius: "8px",
        width: "95vw",
        height: "95vh",
        padding: "20px",
        position: "relative",
        boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        perspective: "1000px",
    },
    closeBtnTopRight: {
        position: "absolute",
        top: "10px",
        right: "15px",
        fontSize: "28px",
        border: "none",
        background: "none",
        cursor: "pointer",
        color: "#333",
    },
    closeBtnBottom: {
        marginTop: "auto",
        alignSelf: "center",
        padding: "10px 25px",
        fontSize: "16px",
        borderRadius: "5px",
        border: "1px solid #333",
        background: "white",
        cursor: "pointer",
        color: "#333",
        transition: "background-color 0.2s ease",
    },
};

const YearlyTrend = () => {
    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];

    const [categories, setCategories] = React.useState(null);
    const [yearlySales, setYearlySales] = React.useState(null);
    const [productPerformance, setProductPerformance] = React.useState(null);
    const [pieData, setPieData] = React.useState(null);
    const [accumulated, setAccumulated] = React.useState(null);
    const [siteHeading, setSiteHeading] = React.useState("Site Performance");
    const [expanded, setExpanded] = React.useState(false);
    const [modalChart, setModalChart] = React.useState(null);

    const sale = React.useRef(null);
    const product = React.useRef(null);
    const total = React.useRef(null);
    const site = React.useRef(null);

    const [title, setTitle] = React.useState("");
    const [salesYAxis, setSalesYAxis] = React.useState("");
    const [productYAxis, setProductYAxis] = React.useState("");
    const [accYAxis, setAccYAxis] = React.useState("");

    const onImageExportClick = (e) => {
        const chart = e.currentTarget.name;
        let chartVisual = null;
        if (chart === "sales") chartVisual = sale.current && exportVisual(sale.current);
        else if (chart === "product") chartVisual = product.current && exportVisual(product.current);
        else if (chart === "total") chartVisual = total.current && exportVisual(total.current);
        else chartVisual = site.current && exportVisual(site.current);

        if (chartVisual) {
            exportPDF(chartVisual, {
                paperSize: "A4",
                landscape: true,
            }).then((dataURI) => saveAs(dataURI, "Yearly Performance.pdf"));
        }
    };

    React.useEffect(() => {
        GetCloudLogin((cred) => {
            let token = cred === "" ? cred : cred.token;
            let sess = GetSession();
            let baseUrl =
                token !== undefined && token !== "" ? sess.CloudURL : configData.SERVER_URL;
            const timeOffset = new Date().getTimezoneOffset() / 60;
            baseUrl += `apiV7/dashboard/getyearlytrends?offset=${timeOffset}&siteId=${sess.SiteID}`;

            const init = {
                method: "GET",
                accept: "application/json",
                headers: {
                    Authorization:
                        "Bearer " + (token === null || token === "" ? GetToken() : token),
                },
            };
            fetch(baseUrl, init)
                .then((response) => response.json())
                .then((d) => {
                    setYearlySales(d.yearlySales);
                    setProductPerformance(d.productPerformance);
                    setAccumulated(d.accumulated);
                    setPieData(d.pieData);
                    setCategories(d.productNames);
                    setTitle(d.title);
                    setSalesYAxis(d.salesY);
                    setProductYAxis(d.productY);
                    setAccYAxis(d.accY);
                    if (d.sites === 1) setSiteHeading("Product Share");
                });
        });
    }, []);

    const renderModalChart = () => {
        if (!modalChart) return null;

        const commonProps = {
            style: {
                height: "85vh",
                width: "100%",
                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
                borderRadius: "10px",
            },
        };

        switch (modalChart) {
            case "sales":
                return (
                    <Chart {...commonProps} ref={sale}>
                        <ChartTitle text={title} font="22Pt bold" color="#00008B" />
                        <ChartLegend position="top" orientation="horizontal" />
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem categories={months} startAngle={45} />
                        </ChartCategoryAxis>
                        <ChartValueAxis>
                            <ChartValueAxisItem title={{ text: salesYAxis }} />
                        </ChartValueAxis>
                        <ChartSeriesDefaults type="line" />
                        <ChartSeries>
                            {yearlySales.map((item, idx) => (
                                <ChartSeriesItem
                                    key={idx}
                                    type="line"
                                    tooltip={{ visible: true }}
                                    data={item.data}
                                    name={item.name}
                                />
                            ))}
                        </ChartSeries>
                    </Chart>
                );
            case "product":
                return (
                    <Chart {...commonProps} ref={product}>
                        <ChartTitle text="Product Performance (Top 5)" font="22Pt bold" color="#00008B" />
                        <ChartLegend position="top" orientation="horizontal" />
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem categories={categories} startAngle={45} />
                        </ChartCategoryAxis>
                        <ChartValueAxis>
                            <ChartValueAxisItem title={{ text: productYAxis }} />
                        </ChartValueAxis>
                        <ChartSeriesDefaults
                            type="column"
                            overlay={{ gradient: "sharpBevel" }}
                            gap={0.3}
                            spacing={0.2}
                        />
                        <ChartSeries>
                            {productPerformance.map((item, idx) => (
                                <ChartSeriesItem
                                    key={idx}
                                    data={item.data}
                                    name={item.name}
                                />
                            ))}
                        </ChartSeries>
                    </Chart>
                );
            case "total":
                return (
                    <Chart {...commonProps} ref={total}>
                        <ChartTitle text="Accumulated Total" font="22Pt bold" color="#00008B" />
                        <ChartLegend position="top" orientation="horizontal" />
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem categories={months} startAngle={45} />
                        </ChartCategoryAxis>
                        <ChartValueAxis>
                            <ChartValueAxisItem title={{ text: accYAxis }} />
                        </ChartValueAxis>
                        <ChartSeriesDefaults
                            type="area"
                            overlay={{ gradient: "sharpBevel" }}
                        />
                        <ChartSeries>
                            {accumulated.map((item, idx) => (
                                <ChartSeriesItem
                                    key={idx}
                                    data={item.data}
                                    name={item.name}
                                />
                            ))}
                        </ChartSeries>
                    </Chart>
                );
            case "site":
                return (
                    <Chart {...commonProps} ref={site}>
                        <ChartTitle text={siteHeading} font="22Pt bold" color="#00008B" />
                        <ChartLegend position="top" orientation="horizontal" />
                        <ChartSeries>
                            <ChartSeriesItem
                                type="pie"
                                overlay={{ gradient: "sharpBevel" }}
                                tooltip={{ visible: true }}
                                data={pieData}
                                categoryField="name"
                                field="share"
                            />
                        </ChartSeries>
                    </Chart>
                );
            default:
                return null;
        }
    };

    const ExportZoomButton = ({ name, onClick, iconClass, ariaLabel }) => (
        <button
            type="button"
            name={name}
            onClick={onClick}
            aria-label={ariaLabel}
            style={buttonStaticStyle}
            className="k-button k-button-icon"
        >
            <i className={`fa ${iconClass}`} aria-hidden="true" />
        </button>
    );

    const ButtonsGroup = ({ exportName, onExportClick, onZoomClick }) => (
        <div
            style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
                marginBottom: 8,
            }}
        >
            <ExportZoomButton
                name={exportName}
                onClick={onExportClick}
                iconClass="fa-download"
                ariaLabel="Export as PDF"
            />
            <ExportZoomButton
                onClick={onZoomClick}
                iconClass="fa-expand"
                ariaLabel="Expand Chart"
            />
        </div>
    );

    return (
        <>
            <ExpansionPanel
                title="Quick View"
                expandIcon="fa fa-plus"
                collapseIcon="fa fa-minus"
                expanded={expanded}
                onAction={(e) => setExpanded(!e.expanded)}
            >
                <Reveal>
                    {expanded && (
                        <ExpansionPanelContent>
                            <DashboardCards period="yearly" />
                        </ExpansionPanelContent>
                    )}
                </Reveal>
            </ExpansionPanel>

            <div className="row" style={{ padding: "5px 10px" }}></div>

            <div className="row mb-3">
                {yearlySales && (
                    <div className="col-6 mb-4">
                        <ButtonsGroup
                            exportName="sales"
                            onExportClick={onImageExportClick}
                            onZoomClick={() => setModalChart("sales")}
                        />
                        <div className="k-card" style={cardStyle3D}>
                            <Chart
                                ref={sale}
                                style={{
                                    height: 350,
                                    filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))",
                                    borderRadius: "10px",
                                }}
                            >
                                <ChartTitle text={title} font="22Pt bold" color="#00008B" />
                                <ChartLegend position="top" orientation="horizontal" />
                                <ChartCategoryAxis>
                                    <ChartCategoryAxisItem categories={months} startAngle={45} />
                                </ChartCategoryAxis>
                                <ChartValueAxis>
                                    <ChartValueAxisItem title={{ text: salesYAxis }} />
                                </ChartValueAxis>
                                <ChartSeriesDefaults type="line" />
                                <ChartSeries>
                                    {yearlySales.map((item, idx) => (
                                        <ChartSeriesItem
                                            key={idx}
                                            type="line"
                                            tooltip={{ visible: true }}
                                            data={item.data}
                                            name={item.name}
                                        />
                                    ))}
                                </ChartSeries>
                            </Chart>
                        </div>
                    </div>
                )}

                {productPerformance && (
                    <div className="col-6 mb-4">
                        <ButtonsGroup
                            exportName="product"
                            onExportClick={onImageExportClick}
                            onZoomClick={() => setModalChart("product")}
                        />
                        <div className="k-card" style={cardStyle3D}>
                            <Chart
                                ref={product}
                                style={{
                                    height: 350,
                                    filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))",
                                    borderRadius: "10px",
                                }}
                            >
                                <ChartTitle
                                    text="Product Performance (Top 5)"
                                    font="22Pt bold"
                                    color="#00008B"
                                />
                                <ChartLegend position="top" orientation="horizontal" />
                                <ChartCategoryAxis>
                                    <ChartCategoryAxisItem categories={categories} startAngle={45} />
                                </ChartCategoryAxis>
                                <ChartValueAxis>
                                    <ChartValueAxisItem title={{ text: productYAxis }} />
                                </ChartValueAxis>
                                <ChartSeriesDefaults
                                    type="column"
                                    overlay={{ gradient: "sharpBevel" }}
                                    gap={0.3}
                                    spacing={0.2}
                                />
                                <ChartSeries>
                                    {productPerformance.map((item, idx) => (
                                        <ChartSeriesItem key={idx} data={item.data} name={item.name} />
                                    ))}
                                </ChartSeries>
                            </Chart>
                        </div>
                    </div>
                )}
            </div>

            <div className="row">
                {accumulated && (
                    <div className="col-6 mb-4">
                        <ButtonsGroup
                            exportName="total"
                            onExportClick={onImageExportClick}
                            onZoomClick={() => setModalChart("total")}
                        />
                        <div className="k-card" style={cardStyle3D}>
                            <Chart
                                ref={total}
                                style={{
                                    height: 350,
                                    filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))",
                                    borderRadius: "10px",
                                }}
                            >
                                <ChartTitle text="Accumulated Total" font="22Pt bold" color="#00008B" />
                                <ChartLegend position="top" orientation="horizontal" />
                                <ChartCategoryAxis>
                                    <ChartCategoryAxisItem categories={months} startAngle={45} />
                                </ChartCategoryAxis>
                                <ChartValueAxis>
                                    <ChartValueAxisItem title={{ text: accYAxis }} />
                                </ChartValueAxis>
                                <ChartSeriesDefaults type="area" overlay={{ gradient: "sharpBevel" }} />
                                <ChartSeries>
                                    {accumulated.map((item, idx) => (
                                        <ChartSeriesItem key={idx} data={item.data} name={item.name} />
                                    ))}
                                </ChartSeries>
                            </Chart>
                        </div>
                    </div>
                )}

                {pieData && (
                    <div className="col-6 mb-4">
                        <ButtonsGroup
                            exportName="site"
                            onExportClick={onImageExportClick}
                            onZoomClick={() => setModalChart("site")}
                        />
                        <div className="k-card" style={cardStyle3D}>
                            <Chart
                                ref={site}
                                style={{
                                    height: 350,
                                    filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))",
                                    borderRadius: "10px",
                                }}
                            >
                                <ChartTitle text={siteHeading} font="22Pt bold" color="#00008B" />
                                <ChartLegend position="top" orientation="horizontal" />
                                <ChartSeries>
                                    <ChartSeriesItem
                                        type="pie"
                                        overlay={{ gradient: "sharpBevel" }}
                                        tooltip={{ visible: true }}
                                        data={pieData}
                                        categoryField="name"
                                        field="share"
                                    />
                                </ChartSeries>
                            </Chart>
                        </div>
                    </div>
                )}
            </div>

            {modalChart &&
                ReactDOM.createPortal(
                    <div
                        style={modalStyles.overlay}
                        onClick={() => setModalChart(null)}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div
                            style={modalStyles.content}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                style={modalStyles.closeBtnTopRight}
                                aria-label="Close zoom modal"
                                onClick={() => setModalChart(null)}
                            >
                                &times;
                            </button>
                            {renderModalChart()}
                            <button
                                style={modalStyles.closeBtnBottom}
                                onClick={() => setModalChart(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default YearlyTrend;
